import React, { useCallback, useMemo, useState } from 'react';
import { useRatesRelayQuery, mapRatesToIRates } from '../../hooks/rates/query/V2';
import { IRate } from '../../ts/interface/rental.interface';
import { connect } from 'react-redux';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import { findAccess, history } from '../../utils';
import { tableColumns } from '../../tableColumns/ratesV2Columns';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import RowItem from '../../components/Redesing/row-item';
import { Autocomplete, TextField } from '@mui/material';
import Chip from '@mui/material/Chip';
import { IColumn } from '../../ts/interface/components/redesing/table.interface';
import { SearchChip } from '../../ts/interface/components/table.interface';
import AddIcon from '@mui/icons-material/Add';
import Card from '@mui/material/Card';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useSystemStore } from '../../store/systemStore';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';

const filterOptions = [
  { value: 'activeRates', label: 'Active' },
  { value: 'pendingRates', label: 'Pending' },
  { value: 'archivedRates', label: 'Archived' },
];

const RatesV2 = ({
  systemLoaded,
  movaticAccess,
  systemAccess,
}: {
  systemLoaded: boolean;
  movaticAccess: number;
  systemAccess: number;
}) => {
  const [pageNumberV2, setPageNumberV2] = useState<number>(0);
  const [sizePerPageV2, setSizePerPageV2] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [sortData, setSortData] = useState<string>('name');
  const [search, setSearch] = useState<string>('');
  const [chips, setChips] = useState<SearchChip[]>([]);
  const [value, setValue] = React.useState<any[]>([]);
  const [exportData, setExportData] = useState<boolean>(false);

  const systemGid = useSystemStore((state) => state.systemInfo.gid);
  const debouncedSearch = useSearchDebounce(search);
  const {
    data: ratesData,
    isLoading,
    isRefetching,
  } = useRatesRelayQuery(
    { systemId: systemGid, name: debouncedSearch || undefined },
    { enabled: systemLoaded && !!systemGid }
  );

  const rates = useMemo(() => {
    if (!ratesData) return [];
    return mapRatesToIRates(ratesData).map((rate) => {
      const r = { ...rate };
      r.active_on = new Date(r.active_on).toDateString();
      if (
        r.archived &&
        new Date(r.active_on).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
      ) {
        r.archived = new Date(r.archived).toDateString();
      }
      return r;
    });
  }, [ratesData]);

  const getAccess = () => {
    return findAccess(systemAccess).includes('rates') || !!movaticAccess;
  };

  const handleStatusChange = useCallback((values: string[]): void => {
    setPageNumberV2(0);
    setSizePerPageV2(DEFAULT_ROWS_PER_PAGE);
    setChips((prevChips) => {
      const valuesFound: string[] = [];

      const newChips = prevChips.filter((chip) => {
        if (chip.field !== 'status') {
          return true;
        }

        const found = values.includes(chip.value as string);

        if (found) {
          valuesFound.push(chip.value as string);
        }

        return found;
      });

      if (values.length === valuesFound.length) {
        return newChips;
      }

      values.forEach((value) => {
        if (!valuesFound.includes(value)) {
          const option = filterOptions.find(
            (option) => option.value.toString() === value
          );
          newChips.push({
            label: 'Status',
            field: 'status',
            value,
            displayValue: option!.label,
          });
        }
      });
      return newChips;
    });
  }, []);

  const handleChipDelete = useCallback((deletedGroup: SearchChip) => {
    setChips((prevChips) => {
      return prevChips.filter((group) => {
        return group.label !== deletedGroup.label;
      });
    });
    setValue([]);
  }, []);

  const columnsDis = useMemo(() => {
    const filterers = value.length > 0 ? value.map((item) => item.value) : ['all'];
    return tableColumns(filterers);
  }, [value]);

  const allRates = useMemo(() => {
    let allRates = rates || [];
    if (value.length > 0) {
      allRates = allRates.filter((rate: IRate) => {
        let rateStatus = 'activeRates';
        if (
          rate.archived &&
          new Date(rate.active_on).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
        ) {
          rateStatus = 'archivedRates';
        } else if (
          new Date(rate.active_on).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)
        ) {
          rateStatus = 'pendingRates';
        } else if (
          new Date(rate.active_on).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)
        ) {
          rateStatus = 'activeRates';
        }
        return value.map((item) => item.value).includes(rateStatus);
      });
    }
    return allRates;
  }, [rates, value]);

  return (
    <Card>
      <PaginationTable
        showFilter
        padding={{ pt: 1, pl: 0, pr: 1 }}
        chips={chips}
        handleChipDelete={handleChipDelete}
        exportData={exportData}
        csvFileName={'Rates.csv'}
        onExport={() => setExportData(false)}
        buttons={[
          {
            label: 'Export',
            startIcon: <FileDownloadIcon />,
            onClick: () => setExportData(true),
          },
          {
            label: 'Add',
            startIcon: <AddIcon />,
            disabled: !getAccess(),
            onClick: () => history.push('/billing/rates/add'),
          },
        ]}
        spaceFilters={
          <>
            <RowItem label={'Status'}>
              <Autocomplete
                multiple
                isOptionEqualToValue={(option, value) => {
                  return option.value === value.value;
                }}
                value={value}
                onChange={(event, newValue) => {
                  setValue([
                    ...newValue.filter((option) =>
                      filterOptions.some((item) => item.label === option.label)
                    ),
                  ]);
                  handleStatusChange([
                    ...newValue
                      .filter((option) =>
                        filterOptions.some((item) => item.label === option.label)
                      )
                      .map((item) => item.value.toString()),
                  ]);
                }}
                options={filterOptions}
                getOptionLabel={(option) => option.label}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.label}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                style={{ width: 340 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiInputBase-root': {
                        padding: 0,
                        paddingLeft: '1.5rem',
                        paddingRight: '1.5rem',
                      },
                      '& .MuiInputLabel-root': {
                        display: 'none',
                      },
                    }}
                  />
                )}
              />
            </RowItem>
          </>
        }
        isLoading={isLoading}
        isLoadingCache={isRefetching}
        localSearch={false}
        noRemoteSort
        disableHasMore
        customKey={'gid'}
        onRowCLick={(id: string) => history.push(`/billing/rates/${id}`)}
        sortBy={sortData}
        onSortChange={(sort) => setSortData(sort.key)}
        columns={columnsDis as IColumn[]}
        items={allRates || []}
        searchPlaceholder={`Search rates`}
        page={pageNumberV2}
        rowsPerPage={sizePerPageV2}
        onPageChange={(page, newPage) => {
          setPageNumberV2(newPage);
        }}
        onRowsPerPageChange={(pageSizeString) => {
          const pageSize = Number(pageSizeString.target.value);
          setSizePerPageV2(pageSize);
        }}
        handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
          setPageNumberV2(0);
          setSearch(e.target.value);
        }}
        searchText={search}
      />
    </Card>
  );
};

export default connect(
  (state: {
    admin: {
      systemAccess: number;
      admin: {
        admin: {
          movaticAccess: number;
        };
      };
    };
    system: {
      isLoaded: boolean;
    };
  }) => ({
    movaticAccess: state.admin.admin.admin.movaticAccess,
    systemAccess: state.admin.systemAccess,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(RatesV2);
